/**
 * Payment routing: every payment type goes to exactly one destination, the firm's TRUST account or
 * its OPERATING account, and the two never mix:
 *  - the firm maps each payment type to a destination (retainers default to trust);
 *  - trust and operating must be different Stripe accounts;
 *  - an invoice freezes its destination and Stripe account when it is created, so later routing
 *    changes never move money that was already requested;
 *  - one Checkout session pays exactly one invoice, on that invoice's account;
 *  - a webhook event is applied only when it came from the account the invoice was frozen to.
 *
 * The firm remains responsible for trust-accounting compliance (law society rules); LawBee only
 * keeps the routing consistent and records a ledger for the bookkeeper.
 */

export const PAYMENT_KINDS = ['retainer', 'invoice', 'consultation_fee'] as const;
export type PaymentKind = (typeof PAYMENT_KINDS)[number];
export type Destination = 'trust' | 'operating';

export const PAYMENT_KIND_LABELS: Record<PaymentKind, string> = {
  retainer: 'Retainer (money held for future fees)',
  invoice: 'Invoice for fees earned',
  consultation_fee: 'Consultation fee',
};

export type PaymentRouting = Record<PaymentKind, Destination>;

export const DEFAULT_ROUTING: PaymentRouting = { retainer: 'trust', invoice: 'operating', consultation_fee: 'operating' };

export function resolveRouting(saved: Partial<Record<string, string>> | null | undefined): PaymentRouting {
  const out = { ...DEFAULT_ROUTING };
  for (const k of PAYMENT_KINDS) {
    const v = saved?.[k];
    if (v === 'trust' || v === 'operating') out[k] = v;
  }
  return out;
}

export type PaymentAccount = { destination: Destination; stripeAccountId: string | null; label: string };

export class RoutingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RoutingError';
  }
}

/** Validate the firm's accounts: trust and operating must never be the same Stripe account. */
export function validateAccounts(accounts: PaymentAccount[]): string[] {
  const problems: string[] = [];
  const trust = accounts.find((a) => a.destination === 'trust');
  const operating = accounts.find((a) => a.destination === 'operating');
  for (const a of accounts) {
    if (a.stripeAccountId && !/^acct_[A-Za-z0-9]+$/.test(a.stripeAccountId)) problems.push(`${a.label}: Stripe account ids look like acct_...`);
  }
  if (trust?.stripeAccountId && operating?.stripeAccountId && trust.stripeAccountId === operating.stripeAccountId) {
    problems.push('Trust and operating must be different Stripe accounts so trust money is never mixed with operating money.');
  }
  return problems;
}

export type RouteDecision = { destination: Destination; stripeAccountId: string | null };

/**
 * Where a new payment request of `kind` goes. In production (`requireAccounts`) the destination
 * account must be configured; a trust payment never falls back to the operating account or the
 * platform account.
 */
export function routePayment(kind: PaymentKind, routing: PaymentRouting, accounts: PaymentAccount[], options: { requireAccounts: boolean }): RouteDecision {
  const destination = routing[kind];
  const account = accounts.find((a) => a.destination === destination);
  const problems = validateAccounts(accounts);
  if (problems.length) throw new RoutingError(problems[0]!);
  if (!account?.stripeAccountId) {
    if (options.requireAccounts) {
      throw new RoutingError(`No ${destination} account is set up for ${PAYMENT_KIND_LABELS[kind].toLowerCase()} payments. Add it in Settings → Payments.`);
    }
    return { destination, stripeAccountId: null };
  }
  return { destination, stripeAccountId: account.stripeAccountId };
}

/**
 * Whether a Stripe event may be applied to an invoice: the event's connected account (null for the
 * platform account) must be the account the invoice was frozen to, and the metadata destination
 * must match. Anything else is refused (never cross trust and operating).
 */
export function eventMatchesInvoice(
  invoice: { destination: Destination; stripeAccountId: string | null },
  event: { account: string | null; destination: string | null | undefined },
): boolean {
  if ((event.account ?? null) !== (invoice.stripeAccountId ?? null)) return false;
  if (event.destination && event.destination !== invoice.destination) return false;
  return true;
}

/** Stripe payment method types for CAD: card plus Canadian pre-authorized debit. */
export function methodTypes(currency: string): string[] {
  return currency.toLowerCase() === 'cad' ? ['card', 'acss_debit'] : ['card', 'us_bank_account'];
}

export type LedgerRow = {
  date: string;
  invoiceNumber: string;
  kind: string;
  destination: string;
  client: string;
  matter: string;
  amountCents: number;
  status: string;
  method: string;
  stripeAccount: string;
  paymentIntent: string;
};

const csvCell = (v: string | number) => {
  const s = String(v);
  // Neutralise spreadsheet formulas and quote when needed.
  const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  return /[",\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

/** Ledger CSV for the bookkeeper, trust and operating in separate sections. */
export function ledgerCsv(rows: LedgerRow[]): string {
  const header = ['Date', 'Destination', 'Invoice', 'Type', 'Client', 'Matter', 'Amount (CAD)', 'Status', 'Method', 'Stripe account', 'Payment intent'];
  const lines = [header.join(',')];
  for (const dest of ['trust', 'operating'] as const) {
    for (const r of rows.filter((x) => x.destination === dest).sort((a, b) => a.date.localeCompare(b.date))) {
      lines.push(
        [r.date, r.destination, r.invoiceNumber, r.kind, r.client, r.matter, (r.amountCents / 100).toFixed(2), r.status, r.method, r.stripeAccount, r.paymentIntent].map(csvCell).join(','),
      );
    }
  }
  return `${lines.join('\n')}\n`;
}

export const TRUST_COMPLIANCE_NOTICE =
  'Your firm remains responsible for trust-accounting compliance under your law society rules. LawBee routes each payment type to the account you choose and keeps a ledger, but it is not a trust accounting system.';
